/**
 * Confirm-access page - replaces allauth's account/reauthenticate.html and
 * mfa/reauthenticate.html
 *
 * allauth asks for the password (or an authenticator/recovery code) again before
 * a two-factor change when the last sign-in is not recent.
 */
import { Link, TextField } from "@mui/material";
import { useState } from "react";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { renderFieldErrors } from "../components/FieldErrorList";
import {
  CodeField,
  MfaBackLink,
  MfaContent,
  MfaMessages,
  MfaText,
  PostForm,
  PrimaryButton,
} from "../components/MfaParts";
import { useAuthContext, useFieldErrors } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

const TITLE = gettext("Confirm access");
const PASSWORD_INTRO = gettext(
  "To protect your account, please enter your password again.",
);
const MFA_INTRO = gettext(
  "To protect your account, please enter a code from your authenticator app or one of your recovery codes.",
);
const PASSWORD = gettext("Password");
const CONFIRM = gettext("Confirm");

export function ReauthenticatePage() {
  const {
    reauthenticationMethod,
    actionUrl = "",
    redirectField,
    reauthenticationAlternatives = [],
  } = useAuthContext();
  const isPassword = reauthenticationMethod === "password";
  const passwordErrors = useFieldErrors("password");
  const codeErrors = useFieldErrors("code");
  const [password, setPassword] = useState("");

  return (
    <AuthLayout title={TITLE}>
      <MfaContent>
        <MfaMessages />
        <MfaText>{isPassword ? PASSWORD_INTRO : MFA_INTRO}</MfaText>
        <PostForm action={actionUrl}>
          {redirectField?.value && (
            <input
              type="hidden"
              name={redirectField.name}
              value={redirectField.value}
            />
          )}
          {isPassword ? (
            <TextField
              fullWidth
              name="password"
              type="password"
              label={PASSWORD}
              autoComplete="current-password"
              autoFocus
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordErrors.length > 0}
              helperText={renderFieldErrors(passwordErrors)}
              sx={(theme) => ({
                marginBottom: theme.spacing(3),
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              })}
              slotProps={{
                htmlInput: { "data-testid": "reauthenticate-password-input" },
              }}
            />
          ) : (
            <CodeField errors={codeErrors} />
          )}
          <PrimaryButton type="submit" data-testid="reauthenticate-submit">
            {CONFIRM}
          </PrimaryButton>
        </PostForm>
        {reauthenticationAlternatives.map((alternative) => (
          <Link
            key={alternative.url}
            href={alternative.url}
            sx={(theme) => ({
              display: "block",
              textAlign: "center",
              marginTop: theme.spacing(2),
              color: BRAND_COLORS.link,
              fontSize: "0.875rem",
            })}
            data-testid="reauthenticate-alternative"
          >
            {alternative.description}
          </Link>
        ))}
        <MfaBackLink to="index" />
      </MfaContent>
    </AuthLayout>
  );
}
